import React, { forwardRef, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { makeStyles } from '@mui/styles'
import PropTypes from 'prop-types'

import orgamax from 'ampersand-app'
import SettingsIcon from '@assets/images/icons/settings_menu.svg'
import IconPencil from '@assets/images/icons/pencil.svg'
import { Title } from '@/modules/base/components/texts'
import { ButtonIconSquare } from '@/modules/base/components/buttons'
import ClientConfig from '@/modules/client/config'
import { fetchTaxAccountantData } from '@/redux/ducks/settings/taxAccountant'
import config from '@/config'
import BaseDrawer from '@/components/drawers/base-drawer'
import AccountTaxSettings from '@/modules/my-account/tenant-information/settings/account-tax-settings'
import lang from '@/lang'
import moment from 'moment'
import { CLIENT_INVITATION_STATUS } from '@/modules/client/_helpers/getStatusLabel'
import request from '@/modules/base/util/request'
import ChartOfAccounts from '@/modules/settings/components/tax-settings/chart-of-accounts/chart-of-accounts'
import BookkeepingAccountRecords from '@/modules/settings/components/bookkeeping-account-records/bookkeepingAccountRecords'
import { Divider } from '@mui/material'
import clsx from 'clsx'

const { clientDetail: { jumpMarkLinks } } = ClientConfig
const taxOfficeUrl = `${config.resourceHost}taxoffice`

const ClientTaxForm = forwardRef((props, ref) => {
  const { client, refresh, shouldRefresh } = props
  const { companyData = {}, companyAddress = {} } = client || {}

  const classes = useStyles()
  const dispatch = useDispatch()
  const [allStates, setAllStates] = useState({})
  const [taxOffice, setTaxOffice] = useState({ contactList: [], addresseList: [] })
  const [taxOfficeState, setTaxOfficeState] = useState(null)
  const [openDrawer, setOpenDrawer] = useState(false)
  const formattedFiscalYear = companyData.beginningOfFiscalYear ? moment(companyData.beginningOfFiscalYear).format('DD.MM.YYYY') : '-'
  const isOfflineClient = client?.inviteeStatus === CLIENT_INVITATION_STATUS.OFFLINE

  const handleClose = () => {
    setOpenDrawer(false)
    refresh()
  }

  const loadAllStatesWithTaxOffices = () => {
    request(`${config.resourceHost}taxoffice/states`, { auth: true, isConcurrent: true })
      .then(({ body: { data } }) => setAllStates(data))
      .done()
  }

  useEffect(() => {
    if (!isOfflineClient) {
      dispatch(fetchTaxAccountantData())
      loadAllStatesWithTaxOffices()
    }
  }, [shouldRefresh])

  useEffect(() => {
    const { taxOfficeId, zipCode } = companyData
    const states = Object.values(allStates)
    if (states.length > 0 && !isOfflineClient) {
      if (taxOfficeId) {
        if (taxOffice.buFaNr === taxOfficeId) return
        const foundTaxOffice = states.flat().find(taxOfficeItem => taxOfficeItem.buFaNr === taxOfficeId)
        setTaxOfficeState(foundTaxOffice.state)
        setTaxOffice(foundTaxOffice)
      } else if (zipCode) {
        request(`${taxOfficeUrl}/${zipCode}`, { auth: true, isConcurrent: true })
          .then(({ body: { data: { state } } }) => setTaxOfficeState(state))
          .done()
      }
    }
  }, [companyData, allStates])

  const getTaxationTypeText = () => {
    if (!companyData) return
    const { isSmallBusiness, isSubjectToImputedTaxation } = companyData

    if (isSmallBusiness) {
      return lang.generalTaxCategoryTitleOption3
    } else if (!isSmallBusiness && isSubjectToImputedTaxation) {
      return lang.generalTaxCategoryTitleOption1
    } else if (!isSmallBusiness && !isSubjectToImputedTaxation) { return lang.generalTaxCategoryTitleOption2 }
  }

  const taxInformation = client.isAllowed
    ? (<>
      <div className={classes.taxDetailsList}>
        <div className={classes.row}>
          <p>{lang.clientDetail.widgets.taxFormWidget.smallBusinessTitle}</p>
          <p className={classes.value}>{companyData.isSmallBusiness ? lang.generics.yes : lang.generics.no }</p>
        </div>
        <div className={classes.row}>
          <p>{lang.clientDetail.widgets.taxFormWidget.taxTypeTitle}</p>
          <p className={classes.value}>{getTaxationTypeText() || '-'}</p>
        </div>
        <div className={classes.row}>
          <p>{lang.clientDetail.widgets.taxFormWidget.taxNumberTitle}</p>
          <p className={classes.value}>{companyData.taxNumber || '-'}</p>
        </div>
        <div className={classes.row}>
          <p>{lang.clientDetail.widgets.taxFormWidget.taxOfficeTitle}</p>
          <p className={classes.value}>{taxOfficeState}</p>
        </div>
        <div className={classes.row}>
          <p>{lang.clientDetail.widgets.taxFormWidget.vatNumberTitle}</p>
          <p className={classes.value}>{companyData.vatNumber || '-'}</p>
        </div>
        <div className={classes.row}>
          <p>{lang.clientDetail.widgets.taxFormWidget.fiskalYearTitle}</p>
          <p className={classes.value}>{formattedFiscalYear}</p>
        </div>
      </div>
      <div className={classes.buttonWrapper}>
        <ButtonIconSquare icon={IconPencil} label={lang.generics.edit} onClick={() => setOpenDrawer(true)} />
      </div>

    </>)
    : (<>{lang.clientDetail.widgets.notAllowed}</>)
  return (
    <div ref={ref} id={jumpMarkLinks.taxForm} className={classes.wrapper}>
      <Title icon={SettingsIcon} className={classes.titleClass} size='small20'>{lang.clientDetail.widgets.taxFormWidget.headline}</Title>
      {taxInformation}
      <BaseDrawer
        open={openDrawer}
        onClose={handleClose}
        headerChildren={<div>{lang.clientDetail.widgets.taxFormWidget.baseDrawerHeader()}</div>}
        withoutFooter
      >
        <div className={classes.drawerWrapper}>
          <AccountTaxSettings refresh={shouldRefresh} onSave={refresh} client={client} />
          <Divider className={clsx(classes.divider, classes.noMarginTop)} />
          <ChartOfAccounts />
          <Divider className={classes.divider} />
          <BookkeepingAccountRecords />
        </div>
      </BaseDrawer>
    </div>
  )
})

ClientTaxForm.propTypes = {
  client: PropTypes.object,
}

ClientTaxForm.defaultProps = {
  client: {},
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    backgroundColor: theme.white,
    width: '100%',
    height: 'min-content',
    border: theme.border,
    borderRadius: theme.borderRadius,
    padding: 30,
  },
  heading: {
    fontWeight: theme.fontWeights.medium,
    fontSize: theme.fontSizes.large,
  },
  taxDetailsList: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: theme.border,
    padding: '10px 0',

    '&:last-child': {
      borderBottom: 'none',
    },
  },
  buttonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '10px 0',
  },
  value: {
    fontSize: 15,
    fontWeight: theme.fontWeights.semiBold,
  },
  drawerWrapper: {
    maxWidth: 690,
  },
  divider: {
    margin: '30px 0',
  },
  noMarginTop: {
    marginTop: 0,
  },
}))

export default ClientTaxForm
